import type { InsurancePolicy, PreAuthorization, InsuranceClaim } from '../types';

let policyDatabase: InsurancePolicy[] = [
  {
    id: 'policy-1',
    patient_id: 'patient-1',
    policy_number: 'POL-2024-001',
    insurance_company: 'HealthCare Plus',
    policy_type: 'individual',
    coverage_amount: 100000,
    deductible: 1000,
    copay_percentage: 20,
    effective_date: '2024-01-01',
    expiry_date: '2024-12-31',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  },
  {
    id: 'policy-2',
    patient_id: 'patient-2',
    policy_number: 'POL-2024-002',
    insurance_company: 'MediCare Pro',
    policy_type: 'family',
    coverage_amount: 250000,
    deductible: 2000,
    copay_percentage: 15,
    effective_date: '2024-01-15',
    expiry_date: '2024-12-31',
    status: 'active',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

let preauthDatabase: PreAuthorization[] = [
  {
    id: 'preauth-1',
    policy_id: 'policy-1',
    patient_id: 'patient-1',
    doctor_id: 'doctor-1',
    treatment_type: 'Cardiac Surgery',
    estimated_cost: 25000,
    requested_date: '2024-01-20',
    approval_date: '2024-01-22',
    expiry_date: '2024-02-22',
    status: 'approved',
    reference_number: 'PA-2024-001',
    notes: 'Approved for emergency cardiac procedure',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

let claimDatabase: InsuranceClaim[] = [
  {
    id: 'claim-1',
    policy_id: 'policy-1',
    invoice_id: 'invoice-1',
    preauth_id: 'preauth-1',
    claim_number: 'CLM-2024-001',
    claim_date: '2024-01-25',
    treatment_date: '2024-01-24',
    diagnosis_code: 'I25.9',
    treatment_code: 'CPT-33533',
    billed_amount: 25000,
    approved_amount: 20000,
    paid_amount: 20000,
    status: 'paid',
    processed_date: '2024-01-30',
    payment_date: '2024-02-05',
    notes: 'Claim processed successfully',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString()
  }
];

let nextPolicyId = 3;
let nextPreauthId = 2;
let nextClaimId = 2;

export const policyService = {
  async getAll(): Promise<InsurancePolicy[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...policyDatabase];
  },

  async getById(id: string): Promise<InsurancePolicy | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return policyDatabase.find(policy => policy.id === id) || null;
  },

  async create(policyData: Omit<InsurancePolicy, 'id' | 'created_at' | 'updated_at'>): Promise<InsurancePolicy> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const newPolicy: InsurancePolicy = {
      ...policyData,
      id: `policy-${nextPolicyId++}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    policyDatabase.push(newPolicy);
    return newPolicy;
  },

  async update(id: string, updates: Partial<InsurancePolicy>): Promise<InsurancePolicy> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const index = policyDatabase.findIndex(policy => policy.id === id);
    if (index === -1) {
      throw new Error('Policy not found');
    }
    
    const updatedPolicy = {
      ...policyDatabase[index],
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    policyDatabase[index] = updatedPolicy;
    return updatedPolicy;
  },

  async delete(id: string): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const index = policyDatabase.findIndex(policy => policy.id === id);
    if (index === -1) {
      throw new Error('Policy not found');
    }
    
    policyDatabase.splice(index, 1);
  }
};

export const preauthService = {
  async getAll(): Promise<PreAuthorization[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...preauthDatabase];
  },

  async getById(id: string): Promise<PreAuthorization | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return preauthDatabase.find(preauth => preauth.id === id) || null;
  },

  async create(preauthData: Omit<PreAuthorization, 'id' | 'reference_number' | 'created_at' | 'updated_at'>): Promise<PreAuthorization> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const newPreauth: PreAuthorization = {
      ...preauthData,
      id: `preauth-${nextPreauthId++}`,
      reference_number: `PA-${Date.now()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    preauthDatabase.push(newPreauth);
    return newPreauth;
  },

  async update(id: string, updates: Partial<PreAuthorization>): Promise<PreAuthorization> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const index = preauthDatabase.findIndex(preauth => preauth.id === id);
    if (index === -1) {
      throw new Error('Pre-authorization not found');
    }
    
    const updatedPreauth = {
      ...preauthDatabase[index],
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    preauthDatabase[index] = updatedPreauth;
    return updatedPreauth;
  }
};

export const claimService = {
  async getAll(): Promise<InsuranceClaim[]> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return [...claimDatabase];
  },

  async getById(id: string): Promise<InsuranceClaim | null> {
    await new Promise(resolve => setTimeout(resolve, 100));
    return claimDatabase.find(claim => claim.id === id) || null;
  },

  async create(claimData: Omit<InsuranceClaim, 'id' | 'claim_number' | 'created_at' | 'updated_at'>): Promise<InsuranceClaim> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const newClaim: InsuranceClaim = {
      ...claimData,
      id: `claim-${nextClaimId++}`,
      claim_number: `CLM-${Date.now()}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    claimDatabase.push(newClaim);
    return newClaim;
  },

  async update(id: string, updates: Partial<InsuranceClaim>): Promise<InsuranceClaim> {
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const index = claimDatabase.findIndex(claim => claim.id === id);
    if (index === -1) {
      throw new Error('Claim not found');
    }
    
    const updatedClaim = {
      ...claimDatabase[index],
      ...updates,
      updated_at: new Date().toISOString()
    };
    
    claimDatabase[index] = updatedClaim;
    return updatedClaim;
  }
};
